import cx from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import { forwardRef, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { icons } from '../../assets';
import { Image } from '../../common';
import { ModalArea } from '../../common/ModalButtonArea';
const {
  AddPlusIcon,
  FileFolder,
  DeleteIcon,
  GreenTickIcon,
  ModelIcon,
  RetryIcon,
  EditIcon,
  CrossIcon,
} = icons;
import styles from './Upload3DModalButton.module.scss';
import Button from '../Button';
import { useNavigate } from 'react-router-dom';
import {
  ADMIN_SETTINGS,
  NEW_REQUEST_ROUTE,
} from '../../constants/routePathConstants';
import { QuestionMarkCircleIcon } from '../icons/QuestionMarkCircleIcon';
import { errorToast, infoToast, successToast, warningToast } from '../../helpers/toastHelper';
import {
  apiPost3DModelJob,
  apiPostImageOnPresignedData,
  apiPostUpload3DModel,
} from '../../helpers/api';
import {
  persistPendingUpload,
  markFileUploaded,
  markUploadAlive,
  completeUpload,
} from '../../helpers/resumableModelUpload';
import { convertBytesInMB } from '../../helpers/unityHelper';
import Loader from '../Loader/Loader';
import { GoogleDrivePickerButton } from '../../common/UploadButton/GoogleDrivePicker';
import { DropBoxFilePickerButton } from '../../common/UploadButton/DropBoxFilePicker';
import { PermissionArea } from '../../common/PermissionArea';
import { PERMISSION } from '../../constants/permissionConstants';
import {
  ACCEPT_3D_MODELS_IN_ZIP,
  API_UPLOAD_TYPE,
  MODAL_UPLOAD_GUIDELINES_URL,
  MODEL_SIZE_LIMIT,
  SENTRY_ERROR_TYPES,
  SETTINGS_TAB,
  STATUS_DISABLED_KEY,
  STATUS_NOT_DONE_KEY,
  THREE_3D_MODAL_BASE_TYPE,
  THREE_D_VIEWER_ACCEPTED_MIME_TYPES,
  THREED_MODEL_VARIATION_PRODUCT_ID,
  UPLOAD_LIMIT_TYPE_LABEL,
} from '../../constants/constants';
import { useUserContext } from '../../contexts/UserContext';
import Model3DLoader from '../Model3DLoader';
import Model3DLoaderLib from '../Model3DLoaderLib';
import { usePermissionsHandler, useStaticPermissions } from '../../hooks/usePermissions';
import {
  captureSentryException,
  clearCacheFromAPI,
  createModelAutomationRender,
  getExtensionFromURL,
  getLocalStorage,
  getTextSubstring,
  openFormInCaseOfNewOrgAndFreemiumUser,
  removeExifFromMultipleFiles,
  sendModelViewerExceptionToSentry,
  wait,
  windowOpen,
} from '../../helpers/jsHelper';
import Request3DModelImage from '../../assets/images/request_3d_model_image.jpg';
import Upload3DModelImage from '../../assets/images/upload_3d_model_image.jpg';
import useOnClickOutsideRef from '../../hooks/useOnClickOutsideRef';
import { ElipsisTooltip } from '../../common/ElipsisTooltip';
import { isNameValid } from '../../helpers/validationHelper';
import {
  MAX_3D_MODEL_PRODUCT_NAME_LENGTH,
  NAME_EXCLUDED_SPECIAL_CHARS_V2_GLOBAL,
} from '../../constants/validationConstants';
import { useSelector } from 'react-redux';
import {
  MODEL_UPLOAD_TYPE,
  selectUpload3DModelProductShow,
  upload3DModelProductActions,
} from '../../redux/slicers/upload3DModelProductSlicer';
import { dispatcher } from '../../helpers/projectHelper';

//extracting zip file
import { unzip } from 'unzipit';

//gltf validator
import * as GLTFValidator from '../GLTFValidator';
import { apiGetAllProductUsedCount } from '../../api/subscription/subscription';
import useCurrentOrganizationInfo from '../../hooks/useCurrentOrganizationInfo';
import {
  sendRenderForAutomation,
  sendRenderForAutomation360,
} from '../../helpers/textureModelUploadHelper';
import UploadFileModal from '../UploadFileModal';
import { addFiles, selectCompletedFiles, setResetFiles } from '../../redux/slicers/appSlicer';
import { selectFetchModels } from '../../redux/slicers/productV2Slicer';

export const ACCEPT_3D_MODELS =
  '.glb, .gltf, .obj, .fbx, .blend, .usdz, .stl, .dae, .ply, .abc, .bvh, .x3d, .wrl, .3ds, .max, .off, .step, .stp, .iges, .igs, .brep, .ifc, .3mf, .fcstd, .fcstd1, .zip'; // .skp // Removing .skp file support

const ACCEPT_GOOGLE_3D_MODELS =
  'model/gltf-binary,model/obj,application/octet-stream,application/zip,model/vnd.usdz+zip';

export const getExtension = (name = null) => {
  if (!name) return name;
  const ext = name.split('.').pop();
  return ext?.trim()?.toLowerCase();
};

const ButtonUI = forwardRef(({ children, ...rest }, ref) => {
  return (
    <Button size="lg" color="light" {...rest} ref={ref}>
      {children}
    </Button>
  );
});

const downloadFile = async (fileId = null, access_token) => {
  if (!fileId) return null;
  const downloadUrl = `https://www.googleapis.com/drive/v3/files/${fileId}/?supportsTeamDrives=true&alt=media`;

  return new Promise((resolve, reject) => {
    fetch(downloadUrl, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${access_token}`,
      },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        resolve(url);
      })
      .catch((e) => {
        reject(null);
      });
  });
};

const UploadFromGoogleDrive = ({ onUpload, setLoading }) => {
  const onGoogleDriveFileSelect = async (originalFiles, access_token) => {
    setLoading(true);
    const files = await removeExifFromMultipleFiles(originalFiles);
    const finalFiles = await Promise.allSettled(
      files
        ?.filter((a) => ACCEPT_3D_MODELS.includes(getExtension(a.name)))
        .map(async (file) => {
          return {
            ...file,
            blobUrl: await downloadFile(file.id, access_token),
          };
        })
    );

    if (files?.length > finalFiles?.length) {
      warningToast(`Only ${ACCEPT_3D_MODELS.split(',').join(',')} files are supported.`);
    }

    setLoading(false);

    if (finalFiles.filter((a) => a.status === 'fulfilled' && a.value).length > 0) {
      onUpload?.({
        fileList: finalFiles.filter((a) => a.status === 'fulfilled' && a.value).map((a) => a.value),
        access_token,
        type: API_UPLOAD_TYPE.GOOGLE,
      });
    }
  };

  return (
    <GoogleDrivePickerButton
      as={ButtonUI}
      onSelect={onGoogleDriveFileSelect}
      viewMimeTypes="" //{ACCEPT_GOOGLE_3D_MODELS}
      showUploadView={false}
      showUploadFolders={false}
      title="Upload from Google Drive"
    />
  );
};

const UploadFromDropBox = ({ onUpload, setLoading }) => {
  const onDropBoxFileSelect = async (originalFiles) => {
    setLoading(true);
    const files = await removeExifFromMultipleFiles(originalFiles);
    const finalFiles = await Promise.allSettled(
      files
        ?.filter((a) => ACCEPT_3D_MODELS.includes(getExtension(a.name)))
        .map(async (file) => {
          console.log('dropbox file', file);
          let blobresponse = await fetch(file.link);
          let blobdata = await blobresponse.blob();
          console.log('dropbox blobdata', blobdata);
          return {
            ...file,
            blobUrl: URL.createObjectURL(blobdata),
          };
        })
    );

    if (files?.length > finalFiles?.length) {
      warningToast(`Only ${ACCEPT_3D_MODELS.split(',').join(',')} files are supported.`);
    }

    setLoading(false);
    console.log('dropbox file for upload', finalFiles);
    onUpload?.({
      fileList: finalFiles.filter((a) => a.status === 'fulfilled' && a.value).map((a) => a.value),
      type: API_UPLOAD_TYPE.DROP_BOX,
    });

    // onUpload?.({
    //   fileList: files?.filter((a) => ACCEPT_3D_MODELS.includes(getExtension(a.name))),
    //   type: API_UPLOAD_TYPE.DROP_BOX,
    // });
  };

  return (
    <DropBoxFilePickerButton
      as={ButtonUI}
      onSelect={onDropBoxFileSelect}
      extensions=""
      title="Upload from Dropbox"
    />
  );
};

export const Upload3DModalButton = ({ className = '', onSuccess }) => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const [show, setShow] = useState(false);
  const [showUploadModal, setUploadModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fileLoading, setFileLoading] = useState(false);
  const [modelLoading, setModelLoading] = useState(false);
  const [error, setError] = useState(false);
  const [files, setFiles] = useState([]);
  const modRef = useRef(null);
  const modelWrapperRef = useRef(null);
  const listRef = useRef(null);
  const { isNewSubscriptionPlan, updateUserData, isGuestUser, currentUser } = useUserContext();
  const { isFreemiumOrSelfServeUser, hideNewPlans } = useCurrentOrganizationInfo();

  const [threeDModelUploadLimit, setThreeDModelUploadLimit] = useState(0);
  const [uploadAvailableLimit, setUploadAvailableLimit] = useState(threeDModelUploadLimit || 0);
  const { handlePermissionEvent } = usePermissionsHandler();
  const [isEdit, setIsEdit] = useState(null);
  const show3DUploadModal = useSelector(selectUpload3DModelProductShow);
  const fetchModels = useSelector(selectFetchModels);
  const debounceTimeout = useRef(null);
  const { hasPermission } = useStaticPermissions();
  const canAccessV2Page = isGuestUser() || hasPermission(PERMISSION.PRODUCT_PAGE_V2);

  const resetState = () => {
    setError(false);
    setSelected(null);
    setShow(false);
    setUploadModal(false);
    setLoading(false);
    setFileLoading(false);
    setModelLoading(false);
    setThreeDModelUploadLimit(0);
    setFiles([]);
    setUploadAvailableLimit(threeDModelUploadLimit || 0);
    setIsEdit(null);
    dispatcher(upload3DModelProductActions.close());
  };

  useEffect(() => {
    getThreeDVariationCount();
    return () => resetState();
  }, []);

  useOnClickOutsideRef(listRef, () => {
    if (isEdit) {
      setIsEdit(null);
    }
  });

  const getThreeDVariationCount = async () => {
    const response = await apiGetAllProductUsedCount();
    const results = response?.results || [];
    const data = results.filter(
      (val) =>
        val.basemodeltype ===
        (!isNewSubscriptionPlan ? '3D Model variation' : '3D Models Upload Limit')
    );
    let totalCount = 0;
    let usedCount = 0;
    data.map((val) => {
      totalCount = totalCount + val?.total_count;
      usedCount = usedCount + val?.used_count;
    });
    const count = totalCount - usedCount;
    setThreeDModelUploadLimit(count);
    return count;
  };

  const updateModelUploadLimit = async ({ loading = false } = {}) => {
    if (!loading) {
      getThreeDVariationCount();
      // updateUserData();
      return;
    }

    setFileLoading(true);

    let limit = 0;
    let uploadLimit = 0;
    // if (!isNewSubscriptionPlan) {
    const variationCount = await getThreeDVariationCount();
    limit = variationCount - files?.filter((a) => !a?.isUploaded)?.length;
    uploadLimit = variationCount;
    // }
    // else {
    //   const result = await updateUserData();
    //   limit =
    //     result?.organization_detail?.model_upload_limit -
    //     files?.filter((a) => !a?.isUploaded)?.length;
    //   uploadLimit = result?.organization_detail?.model_upload_limit;
    // }
    const count = limit > 0 ? limit : 0;
    setFileLoading(false);
    setUploadAvailableLimit(count > 0 ? count : 0);
    return {
      limit: uploadLimit,
      count,
    };
  };

  const checkUploadPermission = async (callback) => {
    updateModelUploadLimit();
    const result = await handlePermissionEvent(PERMISSION.CAN_UPLOAD_3D_MODEL, () => {
      callback?.();
      return true;
    })();
    if (!result) {
      updateModelUploadLimit();
      return false;
    }
    return true;
  };

  const onFileSelected = (file = null) => {
    setError(false);
    if (!file) {
      setSelected(null);
      return;
    }
    if (loading || !file?.isAvailableToUpload) return;
    setSelected(file);
  };

  const checkForDuplicateName = () => {
    setFiles((prev) =>
      prev.map((f) => {
        if (f?.onSaveFailed) return f;
        const isNameExists =
          prev.findIndex(
            (ff) => ff.id !== f.id && ff.name?.toLowerCase() === f.name?.toLowerCase()
          ) !== -1;

        return {
          ...f,
          isFailed: isNameExists ? 'Name already exists' : '',
        };
      })
    );
  };

  const onDelete = (file = null) => {
    if (!file?.id) return;

    if (loading) {
      warningToast('Please wait until the current upload is completed.');
      return;
    }

    const _files = files
      .filter((f) => f.id !== file?.id)
      .map((f, i) => ({
        ...f,
        isAvailableToUpload: i < threeDModelUploadLimit,
      }));
    setFiles(_files);
    onFileSelected(_files?.[0] || null);
    setTimeout(checkForDuplicateName, 100);
  };

  const onUpload = async ({
    fileList = [],
    access_token = 'it does not work without token :( waiting for backend update',
    type = API_UPLOAD_TYPE.NORMAL,
  }) => {
    fileList = Array.from(fileList);
    if (loading) {
      warningToast('Please wait until the current upload is completed.');
      return;
    }

    const { limit, count } = await updateModelUploadLimit({ loading: true });
    if (limit === 0 || count === 0) {
      warningToast('You have reached the upload limit.');
      return;
    }

    // if (count === 0) {
    //   warningToast('You have reached the upload limit.');
    //   return;
    // }

    fileList = await removeExifFromMultipleFiles(fileList);

    let allowedFiles = [...fileList]
      ?.filter((f) => ACCEPT_3D_MODELS.includes(getExtension(f?.name)))
      .splice(0, count);

    const haveOtherFormat = [...fileList].some(
      (a) => !ACCEPT_3D_MODELS.includes(getExtension(a?.name))
    );
    if (haveOtherFormat) {
      [...fileList]?.forEach((f) => {
        if (!ACCEPT_3D_MODELS.includes(getExtension(f?.name)))
          sendModelViewerExceptionToSentry({
            message: `File "${f.name}" is not supported.`,
            file: f,
          });
      });
      warningToast(`Only ${ACCEPT_3D_MODELS.split(',').join(', ')} is allowed for upload.`);
    }

    if (!haveOtherFormat && fileList?.length > allowedFiles?.length) {
      infoToast(
        `You can only upload ${uploadAvailableLimit} model${uploadAvailableLimit > 1 ? 's' : ''}.`
      );
    }

    //zip validation

    let zipFiles = allowedFiles.filter((file) => getExtension(file.name) === 'zip');
    for (let i = 0; i < zipFiles.length; i++) {
      const file = zipFiles[i];

      // `blobUrl` (Google Drive) or the File itself are both accepted by unzipit.
      const { entries } = await unzip(file.blobUrl || file);
      const filePathList = Object.keys(entries)
        .filter((filePath) => !filePath.includes('__MACOSX'))
        .map((path) => path.replace(/^.*[\\/]/, ''));

      const allowedFileExt = filePathList
        .filter((f) => f.trim() !== '' && ACCEPT_3D_MODELS_IN_ZIP.includes(getExtension(f)))
        .splice(0, count);

      file.isValidZip = allowedFileExt.length === 1;
      if (allowedFileExt.length === 1 && ['blend'].includes(getExtension(allowedFileExt[0]))) {
        file.isInstantPreviewNotSupported = true;
      }
    }

    if (zipFiles.length > 0) {
      console.log('zipfiles', zipFiles);

      allowedFiles = [...allowedFiles].filter((f) => {
        if (f.isValidZip === undefined) return true;
        else {
          if (!f.isValidZip) {
            if (!f.validationErr)
              errorToast(
                `File "${f.name}" contains invalid model data. Kindly refer to upload guideline for zip.`
              );
            else errorToast(`File "${f.name}" "${f.validationErr}"`);

            sendModelViewerExceptionToSentry({
              message: `Zip File "${f.name}" is not Valid Zip file.`,
              file: f,
            });
          }

          return f.isValidZip;
        }
      });
      console.log('all files', allowedFiles);
    }

    //************************************************************************************** *

    const _files = [];

    [...allowedFiles].forEach((f, i) => {
      // if (files?.findIndex((ff) => ff?.filename === f?.name) > -1) {
      //   errorToast(
      //     `File "${getTextSubstring(
      //       f.name
      //     )}" already exists in your library. Please rename your file and try again.`
      //   );
      //   return;
      // }

      const size = convertBytesInMB(f?.size || f?.sizeBytes || f?.bytes);
      if (size >= MODEL_SIZE_LIMIT) {
        sendModelViewerExceptionToSentry({
          message: `File "${f.name}" is too large. Maximum file size is ${MODEL_SIZE_LIMIT} MB.`,
          file: f,
        });
        // checking if file size is greater than 20 0MB
        errorToast(`File "${f.name}" is too large. Maximum file size is ${MODEL_SIZE_LIMIT} MB.`);
        return;
      }

      let name = f?.name
        ?.split('.')?.[0]
        ?.trim()
        ?.replaceAll(NAME_EXCLUDED_SPECIAL_CHARS_V2_GLOBAL, '');
      name = name?.substring(0, MAX_3D_MODEL_PRODUCT_NAME_LENGTH - 4);

      const isAvailableToUpload = true; //i < _uploadAvailableLimit;

      _files.push({
        id: uuidv4(),
        fildId: f?.id || uuidv4(),
        name: name,
        filename: f.name?.trim(),
        extension: getExtension(f.name),
        size,
        file: f,
        url: f?.link || f?.blobUrl || URL.createObjectURL(f),
        isUploaded: false,
        isFailed: '',
        isAvailableToUpload,
        access_token,
        type,
      });
    });
    setFiles((prev) => [...prev, ...new Set(_files)]);
    onFileSelected(_files?.[0]);
    setTimeout(checkForDuplicateName, 100);
  };

  useEffect(() => {
    if (!showUploadModal) return;
    const count = threeDModelUploadLimit - files?.filter((f) => f.isAvailableToUpload).length || 0;
    setUploadAvailableLimit(count > 0 ? count : 0);
  }, [threeDModelUploadLimit, files, showUploadModal]);

  useEffect(() => {
    if (!isEdit) setTimeout(checkForDuplicateName, 100);
  }, [isEdit]);

  useEffect(() => {
    // Clear any existing timeout to debounce
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    // Set a new timeout for 100 ms
    debounceTimeout.current = setTimeout(() => {
      if (fetchModels) onSuccess();
    }, 1000);

    // Cleanup timeout on unmount
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [fetchModels]);

  const handleNameUpdate = (id, name = '') => {
    if (!id) return;

    if (!isNameValid(name)) return;

    if (name?.length > MAX_3D_MODEL_PRODUCT_NAME_LENGTH) {
      warningToast(`Name cannot be more than ${MAX_3D_MODEL_PRODUCT_NAME_LENGTH} characters`);
      return;
    }

    const isFailed =
      files?.findIndex(
        (a) => a.id !== id && a.name?.trim()?.toLowerCase() === name?.trim()?.toLowerCase()
      ) > -1;

    setFiles((prev) => [
      ...prev.map((a) => {
        if (a.id === id) {
          return {
            ...a,
            name,
            filename: `${name}.${a?.extension}`,
            isFailed: isFailed ? 'Name already exists.' : null,
            onSaveFailed: false,
          };
        }
        return a;
      }),
    ]);
  };

  const renderModelAutomation = async (results) => {
    const filteredItems = results?.filter((_item) => _item?.status === 'fulfilled') || [];

    if (filteredItems.length > 0) {
      await Promise.all(
        filteredItems.map(async (_item) => {
          // Ensure both calls complete for each item
          createModelAutomationRender({ data: _item?.value?.data });
        })
      );
    }
  };

  const handleSaveToLibrary = async () => {
    if (files.length === 0) {
      warningToast('Please upload atleast one 3D model.');
      return;
    }

    if (threeDModelUploadLimit === 0) return;

    const anyFailed = files.some((a) => a.isFailed);
    if (anyFailed) {
      errorToast('Please fix error and try again.');
      setLoading(false);
      return;
    }

    setLoading(true);
    const allowed = await checkUploadPermission();
    if (!allowed) {
      setLoading(false);
      return;
    }

    const { limit } = await updateModelUploadLimit({ loading: true });
    if (limit === 0) {
      warningToast('You have reached the upload limit.');
      setLoading(false);
      return;
    }

    setIsEdit(null);

    const result = await Promise.allSettled(
      files
        .filter((a) => a.isAvailableToUpload && !a.isUploaded && !a.isFailed)
        .map(async ({ id, fildId, name, filename, file, type, url, access_token }) => {
          const formData = new FormData();
          formData.append('name', name);
          formData.append('main_asset_name', name?.replaceAll(' ', '_'));

          formData.append('upload_type', type);
          formData.append('access_token', access_token);

          if (type === API_UPLOAD_TYPE.GOOGLE) {
            formData.append('filename', filename);
            formData.append('file_id', fildId);
          } else if (type === API_UPLOAD_TYPE.DROP_BOX) {
            formData.append('filename', url);
            formData.append('file_id', fildId);
          } else {
            formData.append(
              'prop_data_user_file_name',
              filename?.replaceAll(' ', '_')?.replaceAll(NAME_EXCLUDED_SPECIAL_CHARS_V2_GLOBAL, '')
            );
          }

          let response;
          try {
            clearCacheFromAPI();
            response = await apiPostUpload3DModel(formData);

            if (response?.status && response?.data?.id) {
              const _status =
                type === API_UPLOAD_TYPE.GOOGLE ? STATUS_DISABLED_KEY : STATUS_NOT_DONE_KEY;

              if (type === API_UPLOAD_TYPE.NORMAL) {
                // Persist the upload so it can be resumed if interrupted before completion.
                // (NORMAL only — Google/Dropbox are pulled server-side and aren't resumable here.)
                await persistPendingUpload({
                  productId: response?.data?.id,
                  file,
                  productData: response?.data,
                  meta: {
                    tempId: id,
                    name,
                    filename,
                    extension: getExtension(filename),
                    uploadType: type,
                    status: _status,
                    orgId: currentUser?.organization,
                    userId: currentUser?.user?.id,
                    now: Date.now(),
                  },
                });

                // The progress callback keeps the resume record claimed while the transfer is live,
                // so the 10s resume poll doesn't start a duplicate upload of the same file.
                await apiPostImageOnPresignedData(
                  file,
                  response?.data?.presigned_url_prop_user_file,
                  () => markUploadAlive(response?.data?.id)
                );
                // File is in S3 — only the job-send remains.
                await markFileUploaded(response?.data?.id);
              }
              const _formData = new FormData();
              _formData.append('product', response?.data?.id);
              _formData.append('status', _status);

              // Removing .skp file support
              // if (getExtensionFromURL(response?.data?.prop_data_user_file) === '.skp' || getExtensionFromURL(filename) === '.skp') {
              //   // Skp File render
              //   _formData.append('is_skp', true);
              // }

              const jobRes = await apiPost3DModelJob(_formData);

              // Uploaded AND job-send actually succeeded -> remove the resumable record.
              // NOTE: api() RESOLVES with `undefined` on a network error (it doesn't reject), so
              // we check for a real response — not just that it resolved — otherwise an offline
              // job-send would wrongly delete the record. If it didn't send, the record stays and
              // resume re-sends the job when back online.
              if (type === API_UPLOAD_TYPE.NORMAL && jobRes) {
                await completeUpload(response?.data?.id);
              }

              setFiles((prev) => [
                ...prev.map((a) => {
                  if (a.id === id) {
                    return { ...a, isUploaded: true };
                  }
                  return a;
                }),
              ]);
            }
          } catch (e) {
            let message = e?.message;
            if (message?.toLowerCase()?.includes('already exists')) {
              message = `The model with name "${getTextSubstring(
                name
              )}" is already exists. Please rename it to upload again.`;
            }

            errorToast(message || 'Something went wrong. Please try again.');

            let failedMessage = '';
            if (message?.toLowerCase()?.includes('already exists')) {
              failedMessage = 'Name already exists in library.';
            }

            setFiles((prev) => [
              ...prev.map((a) => {
                if (a.id === id) {
                  return { ...a, onSaveFailed: true, isFailed: failedMessage || 'Upload failed' };
                }
                return a;
              }),
            ]);

            response = file;
          }
          return response;
        })
    );

    updateModelUploadLimit();

    if (result.length > 0) {
      const success = result.every((res) => res?.status === 'fulfilled' && res?.value);
      const someSuccess = result.some((res) => res?.status === 'fulfilled' && res?.value);
      if (success) {
        successToast('Models are uploaded successfully.');
        if (canAccessV2Page) await renderModelAutomation(result);
        resetState();
        onSuccess?.();
      } else if (someSuccess) {
        warningToast('Some of the Models are not uploaded successfully. Fix them to upload again.');
        if (canAccessV2Page) await renderModelAutomation(result);
        onSuccess?.();
      }

      if (!success) {
        result.forEach((res) => {
          if (res.status !== 'fulfilled')
            sendModelViewerExceptionToSentry({
              message: res?.value?.message,
              file: res?.value?.data,
            });
        });
      }
    } else {
      // warningToast('No Model is uploaded.');
    }

    setLoading(false);
    dispatcher(upload3DModelProductActions.close());
  };

  useEffect(() => {
    if (showUploadModal && selected?.url) {
      modRef?.current?.addEventListener('load', () => {});
    }

    return () => {
      modRef?.current?.removeEventListener('load', () => {
        setSelected(null);
        setError(false);
      });
    };
  }, [showUploadModal, selected]);

  const handleClose = () => {
    setShow(null);
    dispatcher(upload3DModelProductActions.close());
  };

  const buyMore3DModels = () => {
    if (hideNewPlans || isFreemiumOrSelfServeUser) {
      // open enterprise form in case of freemium user whose org is created after 10th Feb 2025
      openFormInCaseOfNewOrgAndFreemiumUser();
      return;
    }
    // redirecting user to the addons page for new / old subscription user as the user can buy model limit / model variation from the addons tab only
    let url = null;
    if (isFreemiumOrSelfServeUser) {
      //https://app.clickup.com/t/3326989/DEV-32992
      url = `${ADMIN_SETTINGS}?activeTab=${SETTINGS_TAB.PLANS}&${UPLOAD_LIMIT_TYPE_LABEL}=${THREE_3D_MODAL_BASE_TYPE}`;
    } else {
      url = `${ADMIN_SETTINGS}?activeTab=${SETTINGS_TAB.ADD_ONS}`;
    }
    // if (!isNewSubscriptionPlan || isFreemiumOrSelfServeUser) {
    //   url = `${ADMIN_SETTINGS}?activeTab=${SETTINGS_TAB.ADD_ONS}`
    // } else {
    //   url = `${ADMIN_SETTINGS}?activeTab=${SETTINGS_TAB.PLANS}&browsePlan=true`
    // }
    windowOpen(url, '_blank');
  };

  const handleFileChange = async (files) => {
    dispatcher(setResetFiles());
    let _files = Array.from(files);
    _files = await removeExifFromMultipleFiles(_files);
    const __files = {};
    _files?.forEach((file) => {
      file.id = uuidv4();
      __files[file.id] = file;
    });
    dispatcher(addFiles({ files: __files, model: true }));
    setUploadModal(false);
  };

  return (
    <>
      {/* model for choose type of model */}
      <ModalArea
        size="lg"
        showModal={show || show3DUploadModal === MODEL_UPLOAD_TYPE.REQUEST}
        setShowModal={() => (loading ? null : handleClose())}
        animation={true}
        centered={true}
        title="New 3D Model"
        headingLeft={true}
        {...(loading ? { backdrop: 'static' } : {})}
        body={
          <>
            <div className={styles.container}>
              <div className={styles.grid}>
                <div className={cx(styles.card)} onClick={() => navigate(NEW_REQUEST_ROUTE)}>
                  <Image src={Request3DModelImage} ratio={1.48} />
                  <div className={styles.info}>
                    <h2 className={styles.title}>Request 3D model</h2>
                    <p className={styles.text}>
                      Choose this option if you want us to create the 3D model of your product.
                    </p>
                  </div>
                </div>
                <div
                  className={cx(styles.card)}
                  onClick={() => {
                    handleClose();
                    setUploadModal(true);
                  }}>
                  <Image src={Upload3DModelImage} ratio={1.48} />
                  <div className={styles.info}>
                    <h2 className={styles.title}>Upload 3D model</h2>
                    <p className={styles.text}>
                      Choose this option if you already have a 3D model file created with you and
                      upload it on the platform.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </>
        }
      />

      {canAccessV2Page ? (
        <UploadFileModal
          showModal={showUploadModal}
          onClose={() => setUploadModal(false)}
          title={'Upload 3D model'}
          acceptedFiles={ACCEPT_3D_MODELS}
          handleFileUpload={handleFileChange}
          supportZip
          supportedFilesText={ACCEPT_3D_MODELS}
        />
      ) : (
        <>
          {/* model for uploading 3D Model file */}
          <ModalArea
            size="xl"
            showModal={showUploadModal}
            setShowModal={() => (loading ? null : resetState(null))}
            animation={true}
            centered={true}
            title="Upload 3D model"
            headingLeft={true}
            backdropClassName="upload-3d-modal-backdrop"
            parentClassName="upload-3d-modal"
            {...(loading ? { backdrop: 'static' } : {})}
            body={
              <>
                <Loader loading={fileLoading} position="absolute" />
                <div className={styles.container}>
                  <div className={styles.uploadWrapper}>
                    <div
                      className={styles.uploadSection}
                      onDragOver={(e) => {
                        e.preventDefault();
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        const _files = e.dataTransfer.files;
                        const _filteredFiles = [..._files]?.filter((a) =>
                          ACCEPT_3D_MODELS.includes(getExtension(a?.name))
                        );
                        if ([..._files]?.length > _filteredFiles?.length) {
                          warningToast(
                            `Only ${ACCEPT_3D_MODELS.split(',').join(',')} files are supported.`
                          );
                        }
                        onUpload({ fileList: _filteredFiles });
                      }}>
                      <div className={styles.upload}>
                        <div
                          className={styles.uploadText}
                          onClick={() => {
                            document.getElementById('inputModelUpload')?.click();
                          }}>
                          <FileFolder width={50} />
                          <h2 className={styles.title}>Upload or drag and drop file</h2>
                          <p className={styles.text}>You can upload models of size upto 1024 MB.</p>
                          <p className={styles.text}>
                            Supported file formats:&nbsp;
                            <strong>
                              {ACCEPT_3D_MODELS.split(',')
                                .filter((a) => !['.zip'].includes(a?.trim()))
                                .join(',')}
                            </strong>{' '}
                          </p>
                          <p className={styles.text}>
                            Zip supported formats:&nbsp;
                            <strong>{ACCEPT_3D_MODELS_IN_ZIP.split(',').join(',')}</strong>
                          </p>
                        </div>
                        <input
                          id="inputModelUpload"
                          type="file"
                          accept={ACCEPT_3D_MODELS}
                          multiple
                          onChange={async (e) => {
                            await onUpload({ fileList: e.target.files });
                            e.target.value = '';
                          }}
                        />
                        <div className="mt-3 d-flex flex-wrap align-items-center justify-content-center gap-3">
                          <UploadFromGoogleDrive onUpload={onUpload} setLoading={setFileLoading} />
                          <UploadFromDropBox onUpload={onUpload} setLoading={setFileLoading} />
                        </div>
                      </div>

                      {files.length > 0 ? (
                        <ul className={styles.list} ref={listRef}>
                          {files.map((f) => (
                            <ListCard
                              key={f.id}
                              {...f}
                              selected={f.id === selected?.id}
                              onFileSelected={() => onFileSelected(f)}
                              onDelete={() => onDelete(f)}
                              onRetry={() => {}}
                              onChange={handleNameUpdate}
                              loading={loading}
                              isEdit={isEdit}
                              setIsEdit={setIsEdit}
                              onBlur={() => setTimeout(checkForDuplicateName, 100)}
                            />
                          ))}
                        </ul>
                      ) : null}
                    </div>
                    <div
                      className={`${styles.modelWrapper} ${!selected?.url ? 'pointer' : ''}`}
                      onClick={() => {
                        if (!selected?.url) document.getElementById('inputModelUpload')?.click();
                      }}
                      onDragOver={(e) => {
                        if (selected?.url) return;
                        e.preventDefault();
                      }}
                      onDrop={(e) => {
                        if (selected?.url) return;
                        e.preventDefault();
                        const _files = e.dataTransfer.files;
                        const _filteredFiles = [..._files]?.filter((a) =>
                          ACCEPT_3D_MODELS.includes(getExtension(a?.name))
                        );
                        if ([..._files]?.length > _filteredFiles?.length) {
                          warningToast(
                            `Only ${ACCEPT_3D_MODELS.split(',').join(',')} files are supported.`
                          );
                        }
                        onUpload({ fileList: _filteredFiles });
                      }}>
                      <Loader loading={modelLoading} position="absolute" />

                      {selected?.url ? (
                        <>
                          <Suspense fallback={<Loader loading={true} position="absolute" />}>
                            {selected?.extension === 'zip' ||
                            THREE_D_VIEWER_ACCEPTED_MIME_TYPES?.includes(selected?.extension) ? (
                              <Model3DLoaderLib
                                url={selected?.url}
                                file={selected}
                                extension={selected?.extension}
                                setModelLoading={setModelLoading}
                                ref={modelWrapperRef}
                              />
                            ) : (
                              <Model3DLoader
                                url={selected?.url}
                                extension={selected?.extension}
                                setModelLoading={setModelLoading}
                                setError={setError}
                                ref={modelWrapperRef}
                              />
                            )}
                            {error ||
                            ['blend'].includes(selected?.extension) ||
                            selected?.file.isInstantPreviewNotSupported ? (
                              <div className={styles.error}>
                                <p className={styles.text}>
                                  Currently, USDC, USD, abc, bvh, x3d, max, stp, igs, fcstd1 
                                  and Blend are not supported in the instant preview. However, you
                                  can still use them and view these formats within the room template
                                  after processing
                                </p>
                              </div>
                            ) : (
                              <span className={styles.dragRotate}>
                                <span>Drag to rotate. Scroll to zoom in and out.</span>
                              </span>
                            )}
                          </Suspense>
                        </>
                      ) : (
                        <div className={styles.info}>
                          <h2 className={cx(styles.title, 'mb-2')}>3D Model Viewer</h2>
                          <p className={styles.text}>
                            Upload your 3D model and view it in the 3D space
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={cx(styles.footer, 'gap-2 justify-content-between')}>
                    <Button
                      size="lg"
                      className="p-0 bg-transparent border-0"
                      color="light"
                      onClick={() => windowOpen(MODAL_UPLOAD_GUIDELINES_URL, '_blank')}>
                      <div className="d-flex alin-items-center gap-1">
                        <QuestionMarkCircleIcon />
                        <span>Model Uploader Guidelines</span>
                      </div>
                    </Button>
                    <p
                      className={cx(
                        styles.text,
                        'mb-0 d-flex align-items-center justify-content-center gap-2 flex-wrap'
                      )}>
                      <span>Upload Limit available : {uploadAvailableLimit}</span>
                      {uploadAvailableLimit <= 0 && (
                        <Button
                          size="lg"
                          className="p-0 ms-2"
                          color="link"
                          onClick={buyMore3DModels}>
                          Buy more
                        </Button>
                      )}
                    </p>
                    <Button
                      size="lg"
                      color={files?.length > 0 ? 'primary' : 'light'}
                      loading={loading}
                      disabled={loading || threeDModelUploadLimit === 0 || files?.length === 0}
                      onClick={handleSaveToLibrary}>
                      Save to library
                    </Button>
                  </div>
                </div>
              </>
            }
          />
        </>
      )}
      <PermissionArea name={PERMISSION.CAN_UPLOAD_3D_MODEL}>
        <Button
          className={cx('py-1 px-3 h-auto ms-2', className)}
          size="sm"
          color="dark"
          onClick={() => checkUploadPermission(() => setShow(true))}>
          <AddPlusIcon color="#fff" /> Add model
        </Button>
      </PermissionArea>
    </>
  );
};

const ListCard = ({
  id,
  name,
  extension,
  size,
  selected = false,
  isUploaded,
  loading,
  isFailed,
  onFileSelected,
  onBlur,
  isAvailableToUpload,
  onDelete,
  onRetry,
  onChange,
  isEdit,
  setIsEdit,
}) => {
  const handleBlur = (v = name) => {
    if (!v) {
      onChange?.(id, 'Model name');
      setIsEdit(null);
    }
    onBlur?.();
  };

  return (
    <li
      className={cx(selected ? styles.selected : '', 'model-list-item')}
      onClick={onFileSelected}
      onMouseLeave={handleBlur}>
      <div className={styles.icon}>
        <ModelIcon />
      </div>
      <div className={styles.info}>
        <ElipsisTooltip title={name}>
          {({ ref, onMouseEnter, onMouseLeave }) => (
            <span
              ref={ref}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              className={cx('ellipsis', styles.name, isEdit === id ? styles.edit : '')}>
              {isEdit === id ? (
                <input
                  className={styles.nameInput}
                  type="text"
                  value={name}
                  autoFocus
                  onChange={(e) => onChange?.(id, e.target.value)}
                  onBlur={(e) => handleBlur(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    setIsEdit(null);
                  }}
                />
              ) : (
                name
              )}
              .{extension}
            </span>
          )}
        </ElipsisTooltip>
        <span
          className={cx(
            'ellipsis',
            styles.size,
            isAvailableToUpload ? '' : styles.failed,
            !isFailed ? '' : styles.failed
          )}>
          {isFailed || `${size} mb`}
        </span>
      </div>
      {/* {!isAvailableToUpload ? (
        <div className={cx(styles.tag, 'ellipsis')}>PAYMENT REQUIRED</div>
      ) : null} */}
      <div className={`${styles.actions} ${loading ? 'opacity-50' : ''}`}>
        {!isUploaded ? (
          isEdit === id ? (
            <CrossIcon
              onClick={() => {
                if (loading) {
                  return;
                }
                setIsEdit(null);
              }}
            />
          ) : (
            <EditIcon
              onClick={() => {
                if (loading) {
                  return;
                }
                setIsEdit(id);
              }}
            />
          )
        ) : null}
        {isUploaded ? (
          <GreenTickIcon />
        ) : (
          <DeleteIcon
            onClick={(e) => {
              if (loading) {
                return;
              }
              e.preventDefault();
              e.stopPropagation();
              onDelete?.();
            }}
          />
        )}
        {/* {onRetry && isFailed ? (
          <RetryIcon
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRetry?.();
            }}
          />
        ) : null} */}
      </div>
    </li>
  );
};

export const NotFound3DModalUI = ({ onSuccess, message }) => {
  return (
    <PermissionArea name={PERMISSION.CAN_UPLOAD_3D_MODEL} elseChildren={message}>
      <div
        className="w-100 m-auto d-flex flex-column justify-content-center align-items-center"
        style={{ maxWidth: '400px' }}>
        <h2 className={styles.title}>Your models live here!</h2>
        <p className={styles.text}>
           All the models that you upload or get created from Imagine will be available here.
        </p>
        <Upload3DModalButton className="ms-0" onSuccess={onSuccess} />
      </div>
    </PermissionArea>
  );
};
